import React, { useMemo, useState } from "react";
import { useAtom } from "jotai";
import { useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";
import { updateItemGroup, updateProduct } from "features/Products/api";
import {
  transformItemsGroup,
  findGroupsAllChildrenIds,
} from "features/Products/helpers";
import { activeItemsGroupAtom, selectedProductAtom } from "atoms/catalogAtom";

import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from "components/Modal";
import { Button } from "components/Button";
import { Radio } from "components/Radio";
import { RegularText } from "components/Text";
import { Accordion } from "components/Accordion";

import styles from "./styles.module.scss";

/**
 * Перемещение товара или группы в другую группу (радио-дерево).
 * mode: "product" | "group"
 */
export const MoveToGroupModal = ({ mode, groups }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  const [activeGroup, setActiveGroup] = useAtom(activeItemsGroupAtom);
  const [selectedProduct, setSelectedProduct] = useAtom(selectedProductAtom);

  const [selectedId, setSelectedId] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const tree = useMemo(() => {
    let list = groups;
    if (mode === "group" && activeGroup) {
      // Нельзя переместить группу в саму себя или в любого из её потомков
      const excluded = new Set([
        String(activeGroup.itemgroupid),
        ...findGroupsAllChildrenIds(groups, [activeGroup.itemgroupid]).map(
          String
        ),
      ]);
      list = groups.filter((g) => !excluded.has(String(g.itemgroupid)));
    }
    return transformItemsGroup(list);
  }, [groups, mode, activeGroup]);

  const toggleAccordion = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMove = async () => {
    if (selectedId == null) return;
    setSaving(true);
    setError("");
    try {
      if (mode === "group") {
        await updateItemGroup({
          groupid: activeGroup.itemgroupid,
          name: activeGroup.name,
          parentid: selectedId,
          importcode: activeGroup.importcode,
          position: activeGroup.position,
          avaliable: activeGroup.avaliable,
          onlywhole: activeGroup.onlywhole,
        });
        setActiveGroup({ ...activeGroup, parentid: selectedId });
        queryClient.invalidateQueries(["item-groups"]);
      } else {
        const p = selectedProduct;
        await updateProduct({
          itemid: p.itemid,
          name: p.name,
          itemgroupid: Number(selectedId),
          avaliable: p.avaliable,
          nodiscount: p.nodiscount,
          service: p.service,
          measureid: p.measureid,
          onlywhole: p.onlywhole,
          discription: p.discription ?? "",
          nobonus: p.nobonus,
          deleted: 0,
          position: p.position,
          upload: p.upload,
          ndsid: p.ndsid,
          importcode: p.importcode,
          freeprice: p.freeprice,
          code: p.code,
          siteimportcode: p.siteimportcode,
        });
        setSelectedProduct(null);
        queryClient.invalidateQueries(["products"]);
      }
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const renderTree = (nodes) =>
    nodes.map((node) => {
      const row = (
        <div className={styles.radioRow} key={node.itemgroupid}>
          <Radio
            id={`move-${node.itemgroupid}`}
            name="move-group"
            label={node.name}
            checked={String(selectedId) === String(node.itemgroupid)}
            onChange={() => setSelectedId(node.itemgroupid)}
          />
        </div>
      );

      if (node.children?.length) {
        return (
          <Accordion
            key={node.itemgroupid}
            withIcon
            arrowZone
            collapsed={!expanded.has(node.itemgroupid)}
            handleCollapse={() => toggleAccordion(node.itemgroupid)}
            parent={row}
          >
            <div className={styles.treeChildren}>
              {renderTree(node.children)}
            </div>
          </Accordion>
        );
      }
      return row;
    });

  return (
    <ModalShell width={420}>
      <ModalHeader
        title="Выберите группу для перемещения"
        onClose={closeModal}
      />
      <ModalBody className={styles.moveModalBody}>
        <div className={styles.radioRow}>
          <Radio
            id="move-root"
            name="move-group"
            label={mode === "group" ? "Все товары (корень)" : "Все товары"}
            checked={String(selectedId) === "0"}
            onChange={() => setSelectedId("0")}
          />
        </div>
        {renderTree(tree)}
        {!!error && (
          <RegularText size={13} color="danger">
            {error}
          </RegularText>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={closeModal}>
          Отменить
        </Button>
        <Button
          variant="secondary"
          onClick={handleMove}
          disabled={selectedId == null}
          loading={saving}
        >
          Перенести
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
